import React, { useState, useEffect } from 'react';
import { interestCategories } from '../lib/supabase';
import opportunityData from '../data/opportunity-matching.json';

export const OpportunityMatcherPage = ({ setPage, goBack, viewerMode = 'student' }) => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({
    interests: [],
    csecSubjects: [],
    location: null,
    availability: null,
    experience: null
  });
  const [matchedOpportunities, setMatchedOpportunities] = useState([]);
  const [selectedInterest, setSelectedInterest] = useState(null);
  const [activeTab, setActiveTab] = useState('match');
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);

  const isStudent = viewerMode === 'student';

  // Load interest categories from Supabase with JSON fallback
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const { data, error } = await interestCategories.getAll();
        if (!error && data && data.length > 0) {
          // Transform database data to expected format
          setCategories(data.map(item => ({
            id: item.id,
            name: item.name,
            description: item.description,
            icon: item.icon || '📌',
            related_careers: item.related_careers || [],
            related_csec: item.related_csec || [],
            related_cape: item.related_cape || [],
            opportunity_types: item.opportunity_types || [],
            key_employers: item.key_employers || []
          })));
        } else {
          // Fallback to JSON data
          setCategories(opportunityData.interest_categories || []);
        }
      } catch (err) {
        console.error('Error loading interest categories:', err);
        setCategories(opportunityData.interest_categories || []);
      }
      setLoading(false);
    };
    loadData();
  }, []);

  const locations = [
    'Kingston & St. Andrew',
    'Montego Bay',
    'Spanish Town',
    'Portmore',
    'Ocho Rios',
    'Mandeville',
    'Other Urban Area',
    'Rural Area',
    'Anywhere / Flexible'
  ];

  const availabilityOptions = [
    { id: 'summer-only', label: 'Summer only (June-August)', hours: 'full' },
    { id: 'part-time-school', label: 'Part-time during school year', hours: 'part' },
    { id: 'weekends', label: 'Weekends only', hours: 'weekend' },
    { id: 'flexible', label: 'Flexible / Any time', hours: 'any' }
  ];

  const experienceOptions = [
    { id: 'none', label: 'No work experience yet', level: 0 },
    { id: 'volunteer', label: 'Some volunteering or helping family', level: 1 },
    { id: 'informal', label: 'Informal work (side hustles, odd jobs)', level: 2 },
    { id: 'formal', label: 'Had a formal job before', level: 3 }
  ];

  const csecSubjects = [
    'English A', 'Mathematics', 'English B', 'Principles of Accounts', 'Principles of Business',
    'Information Technology', 'Biology', 'Chemistry', 'Physics', 'Social Studies',
    'Caribbean History', 'Geography', 'Spanish', 'French', 'Food and Nutrition',
    'Technical Drawing', 'Visual Arts', 'Music', 'Physical Education', 'Economics'
  ];

  const toggleInterest = (interestId) => {
    setAnswers(prev => ({
      ...prev,
      interests: prev.interests.includes(interestId)
        ? prev.interests.filter(i => i !== interestId)
        : [...prev.interests, interestId]
    }));
  };

  const toggleSubject = (subject) => {
    setAnswers(prev => ({
      ...prev,
      csecSubjects: prev.csecSubjects.includes(subject)
        ? prev.csecSubjects.filter(s => s !== subject)
        : [...prev.csecSubjects, subject]
    }));
  };

  const calculateMatches = () => {
    const matches = [];

    categories.forEach(category => {
      if (answers.interests.includes(category.id)) {
        // Calculate match score
        let score = 50; // Base score for interest match

        // Add points for matching subjects
        if (category.related_csec) {
          const subjectMatches = category.related_csec.filter(s =>
            answers.csecSubjects.includes(s)
          ).length;
          score += subjectMatches * 10;
        }

        // Location bonus (if jobs available in their area)
        if (answers.location === 'Kingston & St. Andrew' || answers.location === 'Anywhere / Flexible') {
          score += 10; // Most opportunities in Kingston
        }

        matches.push({
          ...category,
          matchScore: Math.min(score, 100),
          matchReasons: getMatchReasons(category)
        });
      }
    });

    // Sort by match score
    matches.sort((a, b) => b.matchScore - a.matchScore);
    setMatchedOpportunities(matches);
  };

  const getMatchReasons = (category) => {
    const reasons = [];

    if (answers.interests.includes(category.id)) {
      reasons.push('Matches your interests');
    }

    const matchingSubjects = category.related_csec?.filter(s =>
      answers.csecSubjects.includes(s)
    ) || [];
    if (matchingSubjects.length > 0) {
      reasons.push(`Aligns with your CSEC subjects: ${matchingSubjects.join(', ')}`);
    }

    return reasons;
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <div className="text-center py-8">
            <div className="text-6xl mb-4">🎯</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Find Your Perfect Opportunity</h2>
            <p className="text-gray-600 max-w-md mx-auto mb-8">
              {isStudent
                ? "Answer a few questions and we'll match you with internships, jobs, and opportunities that fit YOUR interests and goals."
                : "Help your child find meaningful work experiences that align with their education and career aspirations."}
            </p>
            <button
              onClick={() => setStep(1)}
              className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Get Started
            </button>
          </div>
        );

      case 1:
        return (
          <div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">What interests you?</h2>
            <p className="text-gray-600 mb-6">Select all areas that appeal to you (choose at least 1)</p>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Loading...</p>
              </div>
            ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => toggleInterest(category.id)}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    answers.interests.includes(category.id)
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-3xl mb-2">{category.icon}</div>
                  <div className="font-semibold text-gray-800">{category.name}</div>
                  <div className="text-sm text-gray-600 mt-1">{category.description}</div>
                </button>
              ))}
            </div>
            )}
            <div className="flex justify-between mt-8">
              <button
                onClick={() => setStep(0)}
                className="px-6 py-2 text-gray-600 hover:text-gray-800"
              >
                ← Back
              </button>
              <button
                onClick={() => setStep(2)}
                disabled={answers.interests.length === 0}
                className={`px-6 py-2 rounded-lg font-semibold ${
                  answers.interests.length > 0
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                Next →
              </button>
            </div>
          </div>
        );

      case 2:
        return (
          <div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">What CSEC subjects are you taking?</h2>
            <p className="text-gray-600 mb-6">Select all that apply (helps us find better matches)</p>
            <div className="flex flex-wrap gap-2">
              {csecSubjects.map(subject => (
                <button
                  key={subject}
                  onClick={() => toggleSubject(subject)}
                  className={`px-4 py-2 rounded-lg text-sm transition-all ${
                    answers.csecSubjects.includes(subject)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {subject}
                </button>
              ))}
            </div>
            <div className="flex justify-between mt-8">
              <button
                onClick={() => setStep(1)}
                className="px-6 py-2 text-gray-600 hover:text-gray-800"
              >
                ← Back
              </button>
              <button
                onClick={() => setStep(3)}
                className="px-6 py-2 rounded-lg font-semibold bg-blue-600 text-white hover:bg-blue-700"
              >
                Next →
              </button>
            </div>
          </div>
        );

      case 3:
        return (
          <div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Where are you located?</h2>
            <p className="text-gray-600 mb-6">We'll prioritize opportunities near you</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {locations.map(location => (
                <button
                  key={location}
                  onClick={() => setAnswers(prev => ({ ...prev, location }))}
                  className={`p-4 rounded-lg text-left transition-all ${
                    answers.location === location
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {location}
                </button>
              ))}
            </div>
            <div className="flex justify-between mt-8">
              <button
                onClick={() => setStep(2)}
                className="px-6 py-2 text-gray-600 hover:text-gray-800"
              >
                ← Back
              </button>
              <button
                onClick={() => setStep(4)}
                disabled={!answers.location}
                className={`px-6 py-2 rounded-lg font-semibold ${
                  answers.location
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                Next →
              </button>
            </div>
          </div>
        );

      case 4:
        return (
          <div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">When can you work?</h2>
            <p className="text-gray-600 mb-6">Select your availability</p>
            <div className="space-y-3">
              {availabilityOptions.map(option => (
                <button
                  key={option.id}
                  onClick={() => setAnswers(prev => ({ ...prev, availability: option.id }))}
                  className={`w-full p-4 rounded-lg text-left transition-all ${
                    answers.availability === option.id
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <div className="flex justify-between mt-8">
              <button
                onClick={() => setStep(3)}
                className="px-6 py-2 text-gray-600 hover:text-gray-800"
              >
                ← Back
              </button>
              <button
                onClick={() => setStep(5)}
                disabled={!answers.availability}
                className={`px-6 py-2 rounded-lg font-semibold ${
                  answers.availability
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                Next →
              </button>
            </div>
          </div>
        );

      case 5:
        return (
          <div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">What's your experience level?</h2>
            <p className="text-gray-600 mb-6">Be honest - there are opportunities for everyone</p>
            <div className="space-y-3">
              {experienceOptions.map(option => (
                <button
                  key={option.id}
                  onClick={() => setAnswers(prev => ({ ...prev, experience: option.id }))}
                  className={`w-full p-4 rounded-lg text-left transition-all ${
                    answers.experience === option.id
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <div className="flex justify-between mt-8">
              <button
                onClick={() => setStep(4)}
                className="px-6 py-2 text-gray-600 hover:text-gray-800"
              >
                ← Back
              </button>
              <button
                onClick={() => {
                  calculateMatches();
                  setStep(6);
                }}
                disabled={!answers.experience}
                className={`px-6 py-2 rounded-lg font-semibold ${
                  answers.experience
                    ? 'bg-brand-600 text-white hover:bg-brand-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                See My Matches →
              </button>
            </div>
          </div>
        );

      case 6:
        return (
          <div>
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Your Matched Career Fields</h2>
              <p className="text-gray-600">Based on your interests and background</p>
            </div>

            {matchedOpportunities.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No matches found. Try selecting more interests.</p>
                <button
                  onClick={() => setStep(1)}
                  className="mt-4 text-blue-600 hover:underline"
                >
                  Start Over
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {matchedOpportunities.map((match, idx) => (
                  <div
                    key={match.id}
                    className="bg-white rounded-xl shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow"
                    onClick={() => setSelectedInterest(match)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="text-4xl">{match.icon}</div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-800">{match.name}</h3>
                          <p className="text-gray-600 mt-1">{match.description}</p>
                          <div className="mt-2 flex flex-wrap gap-1">
                            {match.matchReasons.map((reason, ridx) => (
                              <span key={ridx} className="text-xs bg-brand-100 text-brand-700 px-2 py-1 rounded">
                                {reason}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-blue-600">{match.matchScore}%</div>
                        <div className="text-xs text-gray-500">Match</div>
                      </div>
                    </div>

                    <div className="mt-4 pt-4 border-t">
                      <div className="text-sm text-gray-600">
                        <strong>Example careers:</strong> {match.related_careers.slice(0, 3).join(', ')}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        <strong>Key employers:</strong> {match.key_employers.slice(0, 3).join(', ')}
                      </div>
                    </div>

                    <div className="mt-3 text-blue-600 text-sm font-medium">
                      View opportunities in this field →
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-8 text-center">
              <button
                onClick={() => {
                  setStep(0);
                  setAnswers({ interests: [], csecSubjects: [], location: null, availability: null, experience: null });
                  setMatchedOpportunities([]);
                }}
                className="text-gray-600 hover:text-gray-800"
              >
                Start Over
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const tabs = [
    { id: 'match', name: 'Find Matches', icon: '🎯' },
    { id: 'browse', name: 'Browse All', icon: '🔍' },
    { id: 'tips', name: 'Success Tips', icon: '💡' },
    { id: 'employers', name: 'For Employers', icon: '🏢' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-700 text-white py-6 sm:py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={goBack}
            className="mb-3 sm:mb-4 text-purple-100 hover:text-white flex items-center gap-2 text-sm sm:text-base"
          >
            ← Back
          </button>
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">Opportunity Matcher</h1>
          <p className="text-purple-100 text-sm sm:text-base">
            {isStudent
              ? opportunityData.introduction.student_view
              : opportunityData.introduction.parent_view}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-6xl mx-auto px-4 -mt-4">
        <div className="bg-white rounded-lg shadow-md p-1 flex flex-wrap gap-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                if (tab.id === 'match') {
                  setSelectedInterest(null);
                }
              }}
              className={`flex-1 min-w-[100px] py-3 px-4 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                activeTab === tab.id
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span>{tab.icon}</span>
              <span className="hidden sm:inline">{tab.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Match Tab */}
        {activeTab === 'match' && !selectedInterest && (
          <div className="bg-white rounded-xl shadow-md p-6">
            {renderStep()}
          </div>
        )}

        {/* Selected Interest Detail */}
        {selectedInterest && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <button
              onClick={() => setSelectedInterest(null)}
              className="mb-4 text-purple-600 hover:text-purple-800 flex items-center gap-2"
            >
              ← Back to matches
            </button>

            <div className="flex items-center gap-4 mb-6">
              <div className="text-5xl">{selectedInterest.icon}</div>
              <div>
                <h2 className="text-2xl font-bold text-gray-800">{selectedInterest.name}</h2>
                <p className="text-gray-600">{selectedInterest.description}</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-purple-50 rounded-lg p-4">
                <h3 className="font-semibold text-purple-800 mb-3">Career Paths in This Field</h3>
                <ul className="space-y-2">
                  {selectedInterest.related_careers.map((career, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-purple-600 rounded-full"></span>
                      {career}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="font-semibold text-blue-800 mb-3">Helpful CSEC/CAPE Subjects</h3>
                <div className="flex flex-wrap gap-2">
                  {[...(selectedInterest.related_csec || []), ...(selectedInterest.related_cape || [])].map((subject, idx) => (
                    <span key={idx} className="bg-blue-100 text-blue-700 px-3 py-1 rounded-lg text-sm">
                      {subject}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-6">
              <h3 className="font-semibold text-gray-800 mb-3">Types of Opportunities</h3>
              <div className="flex flex-wrap gap-2">
                {selectedInterest.opportunity_types.map((type, idx) => (
                  <span key={idx} className="bg-brand-100 text-brand-700 px-3 py-2 rounded-lg">
                    {type}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <h3 className="font-semibold text-gray-800 mb-3">Key Employers in Jamaica</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {selectedInterest.key_employers.map((employer, idx) => (
                  <div key={idx} className="bg-gray-50 p-3 rounded-lg text-center">
                    {employer}
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 p-4 bg-yellow-50 rounded-lg">
              <h3 className="font-semibold text-yellow-800 mb-2">Next Steps</h3>
              <ol className="space-y-2 text-sm">
                <li>1. Research these employers online and on LinkedIn</li>
                <li>2. Check their careers pages for openings</li>
                <li>3. Prepare your resume highlighting relevant skills</li>
                <li>4. Consider volunteering or interning to gain experience</li>
              </ol>
            </div>
          </div>
        )}

        {/* Browse Tab */}
        {activeTab === 'browse' && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-gray-800">All Career Fields</h2>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Loading career fields...</p>
              </div>
            ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {categories.map(category => (
                <div
                  key={category.id}
                  className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => setSelectedInterest(category)}
                >
                  <div className="text-4xl mb-3">{category.icon}</div>
                  <h3 className="font-bold text-gray-800 mb-2">{category.name}</h3>
                  <p className="text-sm text-gray-600 mb-3">{category.description}</p>
                  <div className="text-purple-600 text-sm font-medium">Explore →</div>
                </div>
              ))}
            </div>
            )}
          </div>
        )}

        {/* Tips Tab */}
        {activeTab === 'tips' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">
                {isStudent ? 'Tips for Landing Opportunities' : 'How to Help Your Child Succeed'}
              </h2>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-800 mb-3">Before Applying</h3>
                  <ul className="space-y-2">
                    {opportunityData.tips_for_students.before_applying.map((tip, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <span className="text-blue-600">→</span>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-brand-50 rounded-lg p-4">
                  <h3 className="font-semibold text-brand-800 mb-3">Standing Out</h3>
                  <ul className="space-y-2">
                    {opportunityData.tips_for_students.standing_out.map((tip, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <span className="text-brand-600">★</span>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mt-6">
                <div className="bg-purple-50 rounded-lg p-4">
                  <h3 className="font-semibold text-purple-800 mb-3">During the Experience</h3>
                  <ul className="space-y-2">
                    {opportunityData.tips_for_students.during_the_experience.map((tip, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <span className="text-purple-600">✓</span>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-orange-50 rounded-lg p-4">
                  <h3 className="font-semibold text-orange-800 mb-3">After the Experience</h3>
                  <ul className="space-y-2">
                    {opportunityData.tips_for_students.after_the_experience.map((tip, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <span className="text-orange-600">→</span>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {!isStudent && (
              <div className="bg-white rounded-xl shadow-md p-6">
                <h2 className="text-xl font-bold text-gray-800 mb-4">Parent Guidance</h2>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-brand-50 rounded-lg p-4">
                    <h3 className="font-semibold text-brand-800 mb-3">Supporting Your Child</h3>
                    <ul className="space-y-2">
                      {opportunityData.parent_guidance.supporting_your_child.map((tip, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <span className="text-brand-600">✓</span>
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-red-50 rounded-lg p-4">
                    <h3 className="font-semibold text-red-800 mb-3">Red Flags to Watch</h3>
                    <ul className="space-y-2">
                      {opportunityData.parent_guidance.red_flags_to_watch.map((flag, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <span className="text-red-600">⚠</span>
                          {flag}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Success Stories */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Success Stories</h2>
              <div className="grid sm:grid-cols-3 gap-4">
                {opportunityData.success_stories.map((story, idx) => (
                  <div key={idx} className="bg-gray-50 rounded-lg p-4">
                    <div className="font-semibold text-gray-800">{story.name}, {story.age}</div>
                    <p className="text-sm text-gray-600 mt-2">{story.story}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Employers Tab */}
        {activeTab === 'employers' && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              {opportunityData.employer_posting_guidelines.title}
            </h2>
            <p className="text-gray-700 mb-6">
              {opportunityData.employer_posting_guidelines.introduction}
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="font-semibold text-blue-800 mb-3">Requirements for Posting</h3>
                <ul className="space-y-2">
                  {opportunityData.employer_posting_guidelines.requirements_for_posting.map((req, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <span className="text-blue-600">✓</span>
                      {req}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-brand-50 rounded-lg p-4">
                <h3 className="font-semibold text-brand-800 mb-3">Benefits for Employers</h3>
                <ul className="space-y-2">
                  {opportunityData.employer_posting_guidelines.benefits_for_employers.map((benefit, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <span className="text-brand-600">★</span>
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-6 bg-purple-50 rounded-lg p-4">
              <h3 className="font-semibold text-purple-800 mb-3">What We Need From You</h3>
              <div className="grid sm:grid-cols-2 gap-2">
                {opportunityData.employer_posting_guidelines.what_we_need_from_employers.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm">
                    <span className="w-2 h-2 bg-purple-600 rounded-full"></span>
                    {item}
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 text-center p-6 bg-gray-50 rounded-lg">
              <p className="text-gray-600 mb-4">Ready to post an opportunity?</p>
              <p className="font-semibold text-purple-600">
                {opportunityData.employer_posting_guidelines.contact}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
